import { dangerouslyInjectHTML } from '@visiba/react-std';
import { forwardRef, HTMLAttributes, ReactNode, Ref } from 'react';
import styled from 'styled-components';

type Mirror = 'Vertical' | 'Horizontal' | true | null;

interface HeadlessIconProps extends HTMLAttributes<SVGSVGElement> {
	children?: ReactNode;

	/**
	 * Reflects the value of the native SVG Element `viewBox` property.
	 */
	viewBox?: string;

	/**
	 * Rotation in degrees of the icon.
	 */
	rotation?: number;

	/**
	 * Use to mirror the icon.
	 */
	mirror?: Mirror;

	/**
	 * Use to center the icon.
	 */
	center?: boolean;

	/**
	 * Reflects the value of the native HTML Element `class` property.
	 */
	className?: string;

	/**
	 * Use to inject HTML into the SVG element.
	 */
	dangerouslySetInnerHTML?: ReturnType<typeof dangerouslyInjectHTML>;
}

const elements = {
	svg: styled.svg<{ $rotation: number; $mirror: Mirror; $center: boolean }>`
		width: 100%;
		height: 100%;
		display: block;
		pointer-events: none;
		fill: currentColor;
		stroke: currentColor;
		flex-shrink: 0;

		${({ $rotation, $mirror, $center }): string | null => {
			// Needs to concat "transform" so they won't interfere with each other
			let transformBuilder = '';

			if ($rotation !== 0) {
				transformBuilder += `rotate(${$rotation}deg) `;
			}

			if ($mirror !== null) {
				const horizontal: boolean = $mirror === 'Horizontal' || $mirror === true || false;
				const vertical: boolean = $mirror === 'Vertical' || $mirror === true || false;

				if (horizontal) {
					transformBuilder += 'scaleX(-1) ';
				}

				if (vertical) {
					transformBuilder += 'scaleY(-1) ';
				}
			}

			if ($center) {
				transformBuilder += 'translateY(50%)';

				return `
					position: relative;
					top: 50%;
					transform: ${transformBuilder.trim()};
					transform-origin: 50% 100%;
					vertical-align: super;
				`;
			} else if (transformBuilder !== '') {
				return `transform: ${transformBuilder.trim()};`;
			}

			return null;
		}}
	`,
};

/**
 * # cellula-icon:HeadlessIcon
 */
function CellulaHeadlessIcon(
	{ children, viewBox, rotation, mirror, center, className, dangerouslySetInnerHTML, ...rest }: HeadlessIconProps,
	ref: Ref<SVGSVGElement>,
): JSX.Element {
	return (
		<elements.svg
			aria-hidden='true'
			data-testid='svg'
			xmlns='http://www.w3.org/2000/svg'
			viewBox={viewBox ?? '0 0 512 512'}
			$rotation={rotation ?? 0}
			$mirror={mirror ?? null}
			$center={center ?? false}
			className={className}
			// biome-ignore lint/security/noDangerouslySetInnerHtmlWithChildren: suppressed due to migration - should be fixed
			dangerouslySetInnerHTML={dangerouslySetInnerHTML}
			{...rest}
			ref={ref}
		>
			{children}
		</elements.svg>
	);
}

const HeadlessIcon = forwardRef(CellulaHeadlessIcon);

export type { HeadlessIconProps };
export { HeadlessIcon };
